// react
import { useMemo } from 'react';

// types
import { Ifr, Pagination } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// mui
import { Grid } from '@mui/material';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';

// ui
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { EmptyProps } from '../../../ui/Empty/Empty';
import { TableHeaders, TableHeadersProps } from '../../../ui/TableHeaders/TableHeaders';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// cards
import { CardIfr } from '../../../cards/CardIfr/CardIfr';

export interface TableIfrsProps {
	ifrs: Ifr[];
	isLoading?: boolean;
	pagination: Pagination;
}

export const TableIfrs = ({ ifrs, isLoading, pagination }: TableIfrsProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');

	// headers
	const headers: TableHeadersProps['headers'] = [
		{
			GridProps: { lg: 2.5 },
			label: t('name'),
			value: 'name',
		},
		{
			GridProps: { lg: 2 },
			label: t('period'),
			value: 'dateStart',
		},
		{
			GridProps: { lg: 1 },
			label: t('currency'),
			value: 'currency',
		},
		{
			GridProps: { lg: 1.5 },
			label: t('status'),
			value: 'status',
		},
		{
			GridProps: { lg: 2 },
			label: t('created'),
			value: 'created',
		},
		{
			GridProps: { lg: 2 },
			label: t('modified'),
			value: 'modified',
		},
		{
			GridProps: { lg: 1 },
			label: '',
			value: 'actions',
		},
	];

	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			actions: [],
			content: {
				children: t('empty.reports.content'),
			},
			IconCircleProps: {
				type: 'ifr',
			},
			title: { children: t('empty.reports.title') },
		};

		emptyProps.actions?.push({
			children: t('addReport'),
			onClick: () => {
				searchParams.set('dialog', 'addReport');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});

		// redefine empty content if search params exist
		if ([...searchParams].length && !paramDialog) {
			emptyProps.content = {
				children: t('empty.default.content'),
			};
			emptyProps.IconCircleProps = {
				icon: null,
			};
		}

		return emptyProps;
	}, [paramDialog, searchParams, setSearchParams, t]);

	const content = useMemo(() => {
		if (isLoading || !ifrs.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{ifrs.map((ifr, i) => {
					return (
						<Grid item key={`ifr-${i}`} xs={12}>
							<CardIfr ifr={ifr} />
						</Grid>
					);
				})}
			</Grid>
		);
	}, [empty, isLoading, ifrs]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Grid container spacing={2}>
					<Grid item xs={12} md={true}>
						<FormSearch InputProps={{ placeholder: t('searchByIdOrName') }} />
					</Grid>
					<Grid item container xs={12} md="auto">
						<BtnFilters filters={[]} />
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter numShowing={ifrs.length} pagination={pagination} />
			</Grid>
		</Grid>
	);
};
